import * as LabelPrimitive from '@radix-ui/react-label';
import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * TextField completo — o gap que o playbook registra no sustenta-ai-app
 * ("TextField parcial: input + padrão de label ad-hoc, sem componente único").
 *
 * Aqui label, controle, texto de apoio e mensagem de erro são um componente só,
 * com `id`/`aria-describedby` amarrados. Hard rule 4: o label é sempre visível;
 * placeholder é exemplo de formato, nunca substituto do rótulo.
 */

export const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => (
  <LabelPrimitive.Root ref={ref} className={cn('text-label text-on-surface', className)} {...props} />
));
Label.displayName = 'Label';

export const inputClasses =
  'h-10 w-full rounded-input border border-border-strong bg-bg-surface px-3 text-body text-on-surface transition-colors duration-fast ease-standard placeholder:text-on-surface-muted hover:border-primary disabled:cursor-not-allowed disabled:bg-bg-subtle disabled:text-on-surface-muted';

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => <input ref={ref} className={cn(inputClasses, className)} {...props} />,
);
Input.displayName = 'Input';

interface FieldProps {
  id: string;
  label: string;
  /** Explicação curta abaixo do controle. */
  apoio?: React.ReactNode;
  /** Mensagem de erro: cita o que falhou, não "campo inválido". */
  erro?: string | null;
  obrigatorio?: boolean;
  className?: string;
  children: (props: { id: string; 'aria-describedby': string | undefined; 'aria-invalid': boolean | undefined }) => React.ReactNode;
}

export function Field({ id, label, apoio, erro, obrigatorio, className, children }: FieldProps) {
  const apoioId = apoio ? `${id}-apoio` : undefined;
  const erroId = erro ? `${id}-erro` : undefined;
  const describedBy = [apoioId, erroId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={id}>
        {label}
        {obrigatorio ? <span className="ml-1 text-danger">*</span> : null}
      </Label>
      {children({ id, 'aria-describedby': describedBy, 'aria-invalid': erro ? true : undefined })}
      {apoio ? (
        <p id={apoioId} className="text-label font-normal text-on-surface-muted">
          {apoio}
        </p>
      ) : null}
      {erro ? (
        <p id={erroId} role="alert" className="text-label text-danger">
          {erro}
        </p>
      ) : null}
    </div>
  );
}
